<template>
  <!-- Images keep their own shape, so a wide screenshot is not cropped; video frames are 16:9. -->
  <div
    class="tw:mb-3 tw:overflow-hidden tw:rounded-lg tw:border tw:border-slate-800 tw:bg-slate-950"
    :class="{ 'tw:aspect-video': media.kind !== 'image' }"
  >
    <!-- A screenshot is too small to read at the panel's width, so it opens full size on click. -->
    <Image
      v-if="media.kind === 'image'"
      :src="media.src"
      :alt="media.alt"
      preview
      class="tw:block"
      image-class="tw:block tw:w-full tw:h-auto"
      :pt="{ previewMask: { 'aria-label': `Enlarge: ${media.alt}` } }"
      @show="logEvent('whats_new_image_enlarged', { item_id: itemId })"
    />

    <!--
      Muted, so browsers let it autoplay. Someone who asked for reduced motion gets the poster and
      the controls instead, and starts it themselves.
    -->
    <video
      v-else-if="media.kind === 'loop'"
      :src="media.src"
      :poster="media.poster"
      :aria-label="media.alt"
      :autoplay="!prefersReducedMotion"
      :controls="prefersReducedMotion"
      muted
      loop
      playsinline
      preload="metadata"
      class="tw:block tw:w-full tw:h-full tw:object-cover"
    />

    <!--
      A thumbnail, not an embedded player: the panel is too narrow to watch in, and an iframe would
      load YouTube, cookies and all, for everyone who opens the panel.
    -->
    <button
      v-else
      type="button"
      class="tw:group tw:relative tw:block tw:w-full tw:h-full tw:p-0 tw:border-0 tw:bg-transparent tw:cursor-pointer"
      :aria-label="`Play video: ${media.alt}`"
      @click="playing = true"
    >
      <img
        :src="media.poster ?? `https://i.ytimg.com/vi/${media.videoId}/hqdefault.jpg`"
        alt=""
        loading="lazy"
        class="tw:block tw:w-full tw:h-full tw:object-cover"
      >
      <span class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:bg-black/20 tw:group-hover:bg-black/30 tw:transition-colors">
        <span class="tw:w-12 tw:h-12 tw:rounded-full tw:bg-indigo-600 tw:flex tw:items-center tw:justify-center tw:shadow-lg tw:group-hover:scale-105 tw:transition-transform">
          <Play class="tw:w-5 tw:h-5 tw:text-white tw:ml-0.5" fill="currentColor" aria-hidden="true" />
        </span>
      </span>
    </button>

    <VideoPlayerModal
      v-if="media.kind === 'youtube'"
      v-model="playing"
      :video-url="`https://www.youtube-nocookie.com/embed/${media.videoId}?autoplay=1&rel=0`"
      :title="title"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, watch } from 'vue'
import { Play } from '@lucide/vue'
import { useMediaQuery } from '@vueuse/core'
import Image from 'primevue/image'
// @ts-expect-error importing untyped JS module into TypeScript
import { logEvent } from '@/services/clients/firebase'
import VideoPlayerModal from '@/components/VideoPlayerModal.vue'
import type { WhatsNewMedia } from './whats-new-items'

const props = defineProps<{
  itemId: string
  title: string
  media: WhatsNewMedia
}>()

const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)')
const playing = ref(false)

watch(playing, now => {
  if (now) {
    logEvent('whats_new_video_played', { item_id: props.itemId })
  }
})
</script>
